import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { integrationCalls, type IntegrationCall } from '@/shared/api/resources';
import { formatDateTime, formatMoney } from '@/shared/lib/format';
import { nomeDoProvedor } from '@/shared/lib/nomeDoProvedor';
import { Alert } from '@/shared/ui/Alert';
import { Badge } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import { Modal } from '@/shared/ui/Modal';
import { useToast } from '@/shared/ui/useToast';

import { identificacao, rotulosDaSituacao } from './rotulosDoRegistro';

/**
 * Pecas comuns da aba Registro de /integracoes e da tela de consultas a bureau.
 *
 * As duas telas leem o mesmo registro, e o que uma chamada e (situacao, detalhe,
 * reenvio) precisa ser dito do mesmo jeito nas duas.
 */

export function CallStatusBadge({ call }: { call: IntegrationCall }) {
  const situacao = rotulosDaSituacao[call.status];
  return (
    <span className="inline-flex flex-wrap gap-1">
      <Badge tone={situacao.tone}>{situacao.label}</Badge>
      {call.simulated ? <Badge tone="warning">Simulada</Badge> : null}
    </span>
  );
}

function Linha({ rotulo, valor }: { rotulo: string; valor: string }) {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-2xs font-semibold uppercase tracking-wide text-ink-500">
        {rotulo}
      </dt>
      <dd className="text-sm text-ink-900">{valor || '-'}</dd>
    </div>
  );
}

function custoDaChamada(call: IntegrationCall): string {
  if (call.simulated) return 'Sem custo (simulada)';
  if (!call.cost) return 'Desconhecido';
  return formatMoney(call.cost);
}

function faturavel(call: IntegrationCall): string {
  if (call.cost_billable === null) return 'Desconhecido, vai para conciliacao';
  return call.cost_billable ? 'Sim' : 'Nao';
}

/** "Ver detalhes": situacao, mensagem, custo e as tentativas que vieram da chamada. */
export function CallDetailModal({
  callId,
  onClose,
  nomeOficial = false,
}: {
  callId: string;
  onClose: () => void;
  /** So a area Integracoes mostra o nome oficial do provedor. */
  nomeOficial?: boolean;
}) {
  const consulta = useQuery({
    queryKey: ['integration-calls', 'detail', callId],
    queryFn: ({ signal }) => integrationCalls.get(callId, signal),
  });
  const call = consulta.data;

  return (
    <Modal
      open
      onClose={onClose}
      title="Detalhes da integracao"
      description="O registro e imutavel: um reenvio vira uma chamada nova, ligada a esta."
      size="lg"
      footer={
        <Button variant="secondary" onClick={onClose}>
          Fechar
        </Button>
      }
    >
      {consulta.isError ? (
        <Alert tone="danger" title="Nao foi possivel carregar a integracao">
          {consulta.error.message}
        </Alert>
      ) : null}
      {consulta.isPending ? <p className="text-sm text-ink-500">Carregando.</p> : null}
      {call ? (
        <div className="flex flex-col gap-4">
          {call.notice ? (
            <Alert tone="warning" title="Chamada simulada">
              {call.notice}
            </Alert>
          ) : null}
          <dl className="grid gap-3 sm:grid-cols-2">
            <Linha rotulo="Tipo da integracao" valor={call.call_type_name} />
            <Linha
              rotulo="Provedor"
              valor={
                nomeOficial
                  ? call.provider_name
                  : nomeDoProvedor(call.provider_key, call.provider_name)
              }
            />
            <Linha rotulo="Situacao" valor={rotulosDaSituacao[call.status].label} />
            <Linha rotulo="Identificacao" valor={identificacao(call)} />
            <Linha rotulo="Documento" valor={call.document} />
            <Linha rotulo="Mensagem" valor={call.message} />
            <Linha rotulo="Codigo do erro" valor={call.error_code} />
            <Linha rotulo="Tentativa" valor={String(call.attempt)} />
            <Linha rotulo="Inicio" valor={formatDateTime(call.started_at)} />
            <Linha rotulo="Fim" valor={formatDateTime(call.finished_at)} />
            <Linha rotulo="Duracao" valor={`${call.duration_ms} ms`} />
            <Linha rotulo="Quem pediu" valor={call.requested_label} />
            <Linha rotulo="Custo" valor={custoDaChamada(call)} />
            <Linha rotulo="Faturavel" valor={faturavel(call)} />
            <Linha rotulo="Chave de idempotencia" valor={call.idempotency_key} />
            <Linha
              rotulo="Pode ser refeita"
              valor={call.retryable ? 'Sim, a falha e passageira' : 'Nao indicado'}
            />
          </dl>
          {call.document_masked ? (
            <p className="text-2xs text-ink-500">
              O documento aparece mascarado porque o seu perfil nao le dados de credito.
            </p>
          ) : null}
          <section className="flex flex-col gap-2">
            <h4 className="text-xs font-semibold text-ink-700">Reenvios</h4>
            {call.resends.length === 0 ? (
              <p className="text-sm text-ink-500">Nenhum reenvio desta integracao.</p>
            ) : (
              <ul className="flex flex-col gap-1.5">
                {call.resends.map((reenvio) => (
                  <li
                    key={reenvio.id}
                    className="flex flex-wrap items-center gap-2 text-sm"
                  >
                    <span className="text-ink-700">Tentativa {reenvio.attempt}</span>
                    <CallStatusBadge call={reenvio} />
                    <span className="text-ink-500">
                      {formatDateTime(reenvio.started_at)}
                    </span>
                    <span className="text-ink-600">{reenvio.message}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      ) : null}
    </Modal>
  );
}

/**
 * "Reenviar" e "Refazer consulta". Quando o reenvio pode ser cobrado, a confirmacao
 * de custo e expressa: a caixa precisa ser marcada, como pede a decisao D-05.
 */
export function ResendDialog({
  call,
  costConfirmation,
  title,
  onClose,
}: {
  call: IntegrationCall;
  costConfirmation: boolean;
  title: string;
  onClose: () => void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [confirmado, setConfirmado] = useState(false);
  const exigeConfirmacao = costConfirmation && !call.simulated;

  const reenviar = useMutation({
    mutationFn: () => integrationCalls.resend(call.id, exigeConfirmacao && confirmado),
    onSuccess: (resultado) => {
      if (resultado.reused) {
        toast.success(resultado.message);
      } else {
        toast.success(resultado.message || 'Integracao reenviada.');
      }
      void queryClient.invalidateQueries({ queryKey: ['integration-calls'] });
      onClose();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  return (
    <Modal
      open
      onClose={onClose}
      title={title}
      description="O reenvio grava uma chamada nova, ligada a esta, com a tentativa seguinte."
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={reenviar.isPending}>
            Cancelar
          </Button>
          <Button
            loading={reenviar.isPending}
            disabled={exigeConfirmacao && !confirmado}
            onClick={() => reenviar.mutate()}
          >
            {title}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <p className="text-sm text-ink-600">
          {call.call_type_name} · {identificacao(call)}
        </p>
        {exigeConfirmacao ? (
          <label className="flex items-start gap-2 text-sm text-ink-700">
            <input
              type="checkbox"
              className="mt-0.5 size-4 accent-[var(--color-brand-500)]"
              checked={confirmado}
              onChange={(event) => setConfirmado(event.target.checked)}
            />
            <span>
              Confirmo que a nova consulta pode ser cobrada pelo fornecedor. Se houver
              consulta valida do mesmo tomador, ela e reaproveitada sem custo.
            </span>
          </label>
        ) : null}
      </div>
    </Modal>
  );
}
